import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import {useDailyTrigger} from "../../../../../hooks/useDailyTrigger";
import {FOUR_SECONDS} from "../../../DeliveredServices/Model/Model";
import HandleChange from "../../../../../Helpers/HandleChange";
import DailyAccessMonitoringCompactRow from "../SubComponent/DailyAccessMonitoringCompactRow";
import {DailyAccessMonitoringWebSocket} from "./DailyAccessMonitoringWebSocket";
import {MODEL} from "../Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * AccessMonitoringApp Hook
 *
 * This custom hook manages the complete access monitoring flow for the application.
 * It handles:
 *
 * - Real-time WebSocket connectivity
 * - Employee access monitoring updates
 * - Zone filtering and search management
 * - Recent access row highlighting
 * - Reconnection lifecycle handling
 * - Modal visibility and selected employee state
 * - Dynamic row rendering logic
 *
 * The hook also synchronizes daily refresh triggers and exposes
 * all handlers and state required by the monitoring UI layer.
 *
 * @returns {Object} Object containing:
 *
 * @returns {boolean} isOnline - Indicates whether the WebSocket connection is active.
 * @returns {Function} setModified - Updates the reconnect trigger state.
 * @returns {Object} item - Current monitoring filter payload.
 * @returns {Function} onFilter - Handles filter field changes.
 * @returns {Array} activeZoneOptions - Available active zone filter options.
 * @returns {string} searchText - Current search text value.
 * @returns {Function} setSearchText - Updates the search text state.
 * @returns {Function} handleSearch - Handles search input updates.
 * @returns {Function} onReset - Resets the search input state.
 * @returns {Object} employees - Employee monitoring dataset.
 * @returns {Function} getRowClassName - Generates row highlight CSS classes.
 * @returns {Function} render - Renders a compact employee monitoring row.
 * @returns {Function} t - Translation function from i18next.
 * @returns {Function} setItem - Updates the monitoring payload state.
 * @returns {boolean|null} showModal - Employee detail modal visibility state.
 * @returns {Function} setShowModal - Updates employee detail modal visibility.
 * @returns {Object|null} itemEmployee - Currently selected employee row.
 * @returns {boolean|null} showReportModal - Report modal visibility state.
 * @returns {Function} setShowReportModal - Updates report modal visibility.
 */
export const AccessMonitoringApp = () => {

    const { t } = useTranslation();
    const [activeZoneOptions, setActiveZoneOptions] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [now, setNow] = useState(Date.now());
    const [isOnline, setIsOnline] = useState(false);
    const [modified, setModified] = useState(false);
    const [item, setItem] = useState(MODEL);
    const [itemEmployee, setItemEmployee] = useState(null);
    const [showModal, setShowModal] = useState(null);
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [showReportModal,setShowReportModal] =  useState(null)

    /**
     * Triggers the daily date update logic.
     *
     * <p>
     * Keeps the current item date synchronized with the
     * daily trigger behavior defined in the custom hook.
     * </p>
     */
    useDailyTrigger({ setItem: setItem });

    /**
     * Initializes the WebSocket connection and exposes
     * the manual reconnection handler.
     */
    const { reconnectWebSocket } = DailyAccessMonitoringWebSocket({
        item,
        isOnline,
        setIsOnline,
        setActiveZoneOptions,
        setEmployees
    });

    /**
     * Updates the current timestamp every second.
     *
     * <p>
     * This is used to evaluate whether a row should
     * be highlighted as a recent entry.
     * </p>
     */
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    /**
     * Resets the modified flag when the WebSocket connection is online.
     *
     * <p>
     * This ensures the reconnect trigger is cleared once
     * the connection is restored successfully.
     * </p>
     */
    useEffect(() => {
        if (isOnline) {
            setModified(false);
        }
    }, [isOnline]);

    /**
     * Attempts to reconnect the WebSocket when the modified flag is set.
     *
     * <p>
     * Once the reconnect attempt is triggered, the modified
     * flag is reset.
     * </p>
     */
    useEffect(() => {
        if (modified) {
            reconnectWebSocket();
            setModified(false);
        }
    }, [modified]);

    /**
     * Handles cafeteria filter changes.
     *
     * <p>
     * Updates the export body and delegates the filter
     * change to the parent handler.
     * </p>
     *
     * @param {Object} e change event
     * @returns {void}
     */
    const onFilter = (e) => {
        handleChange(e);
    };

    /**
     * Renders a compact row for a delivered service record.
     *
     * @param {Object} row delivered service row data
     * @returns {JSX.Element} rendered row component
     */
    const render = (row) => {
        return <DailyAccessMonitoringCompactRow row={row} setItemEmployee={setItemEmployee} t={t} setShowModal={setShowModal} />;
    };

    /**
     * Determines whether a row is considered recent.
     *
     * <p>
     * A row is marked as new if its entry date is within
     * the configured time window relative to the current time.
     * </p>
     *
     * @param {number|string|Date} date entry date value
     * @returns {boolean} true if the row is recent; otherwise false
     */
    const isNew = (date) => {
        const entry = new Date(date).getTime();
        return now >= entry && now <= entry + FOUR_SECONDS;
    };

    /**
     * Builds the CSS class string for a row based on
     * whether it is a recent entry.
     *
     * <p>
     * Applies left and right borders to all recent rows,
     * and top or bottom borders only when the adjacent rows
     * are not also recent. This allows contiguous recent rows
     * to appear as a single highlighted block.
     * </p>
     *
     * @param {number} index current row index
     * @param {Object} row current row data
     * @returns {string} CSS class string for the row
     */
    const getRowClassName = (index, row) => {
        if (!isNew(row?.entryDate)) return "";

        const prev = employees?.content?.[index - 1];
        const next = employees?.content?.[index + 1];

        const prevIsNew = prev && isNew(prev?.entryDate);
        const nextIsNew = next && isNew(next?.entryDate);

        return `
            border-left-secondary-150-2px
            border-right-secondary-150-2px
            ${!prevIsNew ? "border-top-secondary-150-2px" : "border-top-secondary-150"}
            ${!nextIsNew ? "border-bottom-secondary-150-2px" : "border-bottom-secondary-150"}
        `.trim();
    };

    /**
     * Handles search input changes.
     *
     * <p>
     * Updates the export body and stores the new search value
     * in the current filter item.
     * </p>
     *
     * @param {string} value search text entered by the user
     * @returns {void}
     */
    const handleSearch = (value) => {
        setItem({ ...item, search: value });
    };

    /**
     * Resets the local search text value.
     *
     * @returns {void}
     */
    const onReset = () => {
        setSearchText("");
    };

    return {
        isOnline,
        setModified,
        item,
        onFilter,
        activeZoneOptions,
        searchText,
        setSearchText,
        handleSearch,
        onReset,
        employees,
        getRowClassName,
        render,
        t,
        setItem,
        showModal,
        setShowModal,
        itemEmployee,
        showReportModal,
        setShowReportModal
    };
};
